import { useQuery } from '@apollo/client/react';
import { useSearchParams } from 'react-router';
import { ASSET_GROUPS_QUERY } from '@scio3/graphql-client';
import { useTeamStore } from '@/stores/team-store';

interface GroupNode {
  _id: string;
  name: string;
  type?: string;
  path?: string;
  teamId?: string;
  isFolderSecurityEdit?: boolean;
  isFolderSecurityView?: boolean;
  securities?: Array<{ userId: string; role: string }>;
  securityTeams?: string[];
}

interface GroupsQueryResult {
  groups: {
    page: {
      edges: Array<{ node: GroupNode }>;
    };
  };
}

/**
 * Permission gate for the Designer page.
 *
 * Uses the URL's `path` parameter (the folder containing the asset) to check
 * if the current user has access to that folder. The backend groups resolver
 * filters out folders the user lacks access to, so an empty result = deny.
 *
 * Gates both edit flows (asset_id + path) and creation flows (path only).
 */
export function useDesignerPermissionGate() {
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const [searchParams] = useSearchParams();

  // Use the URL's path parameter — this is the folder the asset lives in.
  // More reliable than fetching the asset first because the asset's DB path
  // may be null/undefined for some asset types.
  const urlPath = searchParams.get('path');
  // Engage flows (Kiosk Designer Pro) pass engage=1 — folders use GROUP_TYPES.ENGAGE
  const isEngage = searchParams.get('engage') === '1';
  const groupType = isEngage ? 'ENGAGE' : 'ASSET';

  // Parse path to get parent path and folder name (same pattern as useCurrentGroup)
  const isSubfolder = !!urlPath && urlPath !== '/' && urlPath.length > 1;
  const lastSlash = urlPath ? urlPath.lastIndexOf('/') : -1;
  const parentPath = isSubfolder && lastSlash > 0 ? urlPath.substring(0, lastSlash) : null;
  const folderName = isSubfolder ? (urlPath?.substring(lastSlash + 1) ?? null) : null;

  // Fetch the folder with security fields to check if user has access.
  // Runs for both edit (asset_id present) and creation (path-only) flows.
  const shouldFetchFolder = isSubfolder && !!folderName && !!activeTeamId;
  const { data: groupData, loading: groupLoading } = useQuery<GroupsQueryResult>(
    ASSET_GROUPS_QUERY,
    {
      variables: {
        teamId: activeTeamId,
        query: {
          type: groupType,
          path: parentPath,
          name: folderName,
        },
        first: 1,
      },
      skip: !shouldFetchFolder,
      fetchPolicy: 'network-only',
    },
  );

  const parentFolder = groupData?.groups?.page?.edges?.[0]?.node ?? null;

  // No folder path in URL = root level or no folder context, no security to check
  if (!isSubfolder) {
    return { isAllowed: true, isLoading: false };
  }

  // Still loading folder data
  if (groupLoading) {
    return { isAllowed: false, isLoading: true };
  }

  // Folder not found after query completed.
  // The backend groups resolver filters out folders the user lacks access to,
  // so an empty result means the user doesn't have permission → deny (fail closed).
  if (!parentFolder) {
    return { isAllowed: false, isLoading: false };
  }

  // Folder found = user has access
  return { isAllowed: true, isLoading: false };
}
